import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr';

import { api } from '@/shared/api/http';
import { getAuthState } from '@/shared/auth/store';
import { getValidAccessToken } from '@/shared/auth/token';

import type { Call, CallEnded, CallParticipant, IceServersResponse } from '../types';

import { callState, resetCall, useCallStore, type RemotePeer } from './callStore';
import { getRelayOnlyOverride, IceServerSource, NO_ICE, type IceConfig } from './iceServers';

export const CALLS_HUB_URL = '/hubs/calls';
const KEEP_ALIVE_MS = 15_000;

/** Signaling payloads relayed by the server between two participants. */
export type SignalData =
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | { type: 'ice'; candidate: RTCIceCandidateInit };

interface SignalMessage {
  callId: string;
  fromConnectionId: string;
  fromUserId: string;
  data: SignalData;
}

interface PeerEntry {
  pc: RTCPeerConnection;
  stream: MediaStream;
  pending: RTCIceCandidateInit[];
}

export type CallEndReason = CallEnded | { id: string; outcome: 'replaced' | 'left' };

export interface CallManagerEvents {
  /** The call this tab took part in ended (shown as a toast). */
  onEnded?: (ended: CallEndReason) => void;
  /** Media could not be opened (permissions, no device). */
  onMediaError?: (kind: 'audio' | 'video' | 'screen', error: unknown) => void;
  onError?: (code: string) => void;
}

/** The error code of a failed hub invocation (the server sends `messenger.*` codes). */
export function hubErrorCode(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return /\b(messenger\.[a-z_]+|auth\.[a-z_]+)\b/u.exec(message)?.[1] ?? 'messenger.call_failed';
}

/**
 * WebRTC mesh calls of this tab: one `/hubs/calls` connection for signaling, one RTCPeerConnection per remote
 * participant. The participant who joins sends offers to everyone already in the call (no glare); every connection
 * has a fixed audio and video transceiver, so camera, mute and screen sharing switch tracks without renegotiation.
 */
export class CallManager {
  private connection: HubConnection | null = null;
  private connecting: Promise<HubConnection> | null = null;
  private readonly peers = new Map<string, PeerEntry>();
  /** STUN and TURN (with credentials that expire), asked for before every call. */
  private readonly ice = new IceServerSource(
    () => api.get<IceServersResponse>('/calls/ice-servers'),
    () => ({ isAdmin: getAuthState().user?.isAdmin ?? false, relayOnlyOverride: getRelayOnlyOverride() }),
  );
  private iceConfig: IceConfig = NO_ICE;
  private keepAlive: ReturnType<typeof setInterval> | undefined;
  private cameraTrack: MediaStreamTrack | null = null;
  private screenTrack: MediaStreamTrack | null = null;
  events: CallManagerEvents = {};

  private get me(): string {
    return getAuthState().user?.id ?? '';
  }

  // ---- connection ------------------------------------------------------------------------------------------------

  private async connect(): Promise<HubConnection> {
    if (this.connection?.state === HubConnectionState.Connected) return this.connection;
    this.connecting ??= this.open().finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  private async open(): Promise<HubConnection> {
    const connection =
      this.connection ??
      new HubConnectionBuilder()
        .withUrl(CALLS_HUB_URL, { accessTokenFactory: async () => (await getValidAccessToken()) ?? '' })
        .withAutomaticReconnect()
        .configureLogging(import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error)
        .build();
    if (!this.connection) {
      this.connection = connection;
      connection.on('participantJoined', (payload: { callId: string; participant: CallParticipant }) =>
        this.onParticipantJoined(payload),
      );
      connection.on('participantLeft', (payload: { callId: string; userId: string; connectionId: string }) =>
        this.onParticipantLeft(payload),
      );
      connection.on(
        'mediaChanged',
        (payload: { callId: string; userId: string; audio: boolean; video: boolean; screen: boolean }) =>
          this.onMediaChanged(payload),
      );
      connection.on('signal', (payload: SignalMessage) => void this.onSignal(payload));
      connection.on('callEnded', (payload: CallEnded) => this.end(payload));
      connection.on('callReplaced', (payload: { callId: string }) => {
        if (callState().call?.id === payload.callId) this.end({ id: payload.callId, outcome: 'replaced' });
      });
      connection.onreconnected(() => void this.rejoin());
    }
    if (connection.state === HubConnectionState.Disconnected) await connection.start();
    useCallStore.setState({ connectionId: connection.connectionId });
    return connection;
  }

  // ---- starting, joining, leaving --------------------------------------------------------------------------------

  /** Calls the chat (or joins the call already running there). */
  async start(chatId: string, video: boolean): Promise<void> {
    if (callState().status !== 'idle') return;
    useCallStore.setState({ status: 'starting' });
    try {
      const stream = await this.openMedia(video);
      this.iceConfig = await this.ice.refresh();
      const connection = await this.connect();
      const call = await connection.invoke<Call>('StartCall', chatId, video);
      this.enter(call, stream, video);
    } catch (error) {
      this.stopMedia();
      resetCall();
      this.events.onError?.(hubErrorCode(error));
    }
  }

  /** Accepts a ringing call. */
  async accept(call: Call, video: boolean): Promise<void> {
    useCallStore.getState().removeIncoming(call.id);
    if (callState().status !== 'idle') await this.hangup();
    useCallStore.setState({ status: 'starting' });
    try {
      const stream = await this.openMedia(video);
      this.iceConfig = await this.ice.refresh();
      const connection = await this.connect();
      const joined = await connection.invoke<Call>('JoinCall', call.id, video);
      this.enter(joined, stream, video);
    } catch (error) {
      this.stopMedia();
      resetCall();
      this.events.onError?.(hubErrorCode(error));
    }
  }

  async decline(call: Call): Promise<void> {
    useCallStore.getState().removeIncoming(call.id);
    try {
      await api.post(`/calls/${call.id}/decline`);
    } catch {
      // The call may have ended meanwhile.
    }
  }

  async hangup(): Promise<void> {
    const call = callState().call;
    if (!call) return;
    try {
      await this.connection?.invoke('LeaveCall', call.id);
    } catch {
      await api.post(`/calls/${call.id}/leave`).catch(() => undefined);
    }
    this.end({ id: call.id, outcome: 'left' });
  }

  /** Best effort while the page closes (the hub may not deliver any more). */
  leaveOnUnload(): void {
    const call = callState().call;
    const token = getAuthState().accessToken;
    if (!call || !token) return;
    void fetch(`/api/calls/${call.id}/leave`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => undefined);
  }

  private enter(call: Call, stream: MediaStream | null, video: boolean): void {
    const me = this.me;
    const connectionId = this.connection?.connectionId ?? null;
    const peers: Record<string, RemotePeer> = {};
    for (const participant of call.participants) {
      if (participant.connectionId === connectionId || participant.user.id === me) continue;
      peers[participant.connectionId] = toPeer(participant);
    }
    useCallStore.setState({
      status: 'active',
      call,
      connectionId,
      localStream: stream,
      localVersion: callState().localVersion + 1,
      audioEnabled: !!stream?.getAudioTracks()[0]?.enabled,
      videoEnabled: video && !!this.cameraTrack,
      screenSharing: false,
      peers,
      minimized: false,
    });
    clearInterval(this.keepAlive);
    this.keepAlive = setInterval(() => {
      const current = callState().call;
      if (current && this.connection?.state === HubConnectionState.Connected)
        this.connection.invoke('KeepAlive', current.id).catch(() => undefined);
    }, KEEP_ALIVE_MS);
    // The newcomer offers to everybody already in the call.
    for (const connection of Object.keys(peers)) void this.offer(connection);
  }

  /** After a signaling reconnect the connection id changed: join again and renegotiate with everybody. */
  private async rejoin(): Promise<void> {
    const call = callState().call;
    if (!call || !this.connection) return;
    try {
      this.iceConfig = await this.ice.current();
      const joined = await this.connection.invoke<Call>('JoinCall', call.id, callState().videoEnabled);
      for (const entry of this.peers.values()) entry.pc.close();
      this.peers.clear();
      this.enter(joined, callState().localStream, callState().videoEnabled);
    } catch {
      this.end({ id: call.id, outcome: 'left' });
    }
  }

  /**
   * The signed-in user is gone (sign-out, another account): leave the call and close the signaling connection — it is
   * authenticated as that user and must not serve the next one.
   */
  async disconnect(): Promise<void> {
    const call = callState().call;
    if (call) {
      await this.connection?.invoke('LeaveCall', call.id).catch(() => undefined);
      this.end({ id: call.id, outcome: 'left' });
    }
    useCallStore.setState({ incoming: [] });
    this.ice.forget();
    this.iceConfig = NO_ICE;
    const connection = this.connection;
    this.connection = null;
    this.connecting = null;
    await connection?.stop().catch(() => undefined);
  }

  /** Cleans up after the call ended for this tab. */
  end(ended: CallEndReason): void {
    const call = callState().call;
    useCallStore.getState().removeIncoming(ended.id);
    if (call?.id !== ended.id) return;
    clearInterval(this.keepAlive);
    for (const entry of this.peers.values()) entry.pc.close();
    this.peers.clear();
    this.stopMedia();
    resetCall();
    this.events.onEnded?.(ended);
  }

  // ---- media -----------------------------------------------------------------------------------------------------

  private async openMedia(video: boolean): Promise<MediaStream | null> {
    if (typeof (navigator.mediaDevices as MediaDevices | undefined)?.getUserMedia !== 'function') {
      this.events.onMediaError?.('audio', new Error('unsupported'));
      return new MediaStream();
    }
    const stream = new MediaStream();
    try {
      const audio = await navigator.mediaDevices.getUserMedia({ audio: true });
      audio.getAudioTracks().forEach((track) => stream.addTrack(track));
    } catch (error) {
      this.events.onMediaError?.('audio', error);
    }
    if (video) {
      try {
        const camera = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        this.cameraTrack = camera.getVideoTracks()[0] ?? null;
        if (this.cameraTrack) stream.addTrack(this.cameraTrack);
      } catch (error) {
        this.events.onMediaError?.('video', error);
      }
    }
    return stream;
  }

  private stopMedia(): void {
    callState()
      .localStream?.getTracks()
      .forEach((track) => track.stop());
    this.cameraTrack?.stop();
    this.screenTrack?.stop();
    this.cameraTrack = null;
    this.screenTrack = null;
  }

  toggleAudio(): void {
    const { localStream, audioEnabled } = callState();
    const track = localStream?.getAudioTracks()[0];
    if (!track) {
      this.events.onMediaError?.('audio', new Error('no microphone'));
      return;
    }
    track.enabled = !audioEnabled;
    useCallStore.setState({ audioEnabled: track.enabled });
    this.announceMedia();
  }

  async toggleVideo(): Promise<void> {
    const { localStream, videoEnabled, screenSharing } = callState();
    if (videoEnabled) {
      if (this.cameraTrack) {
        localStream?.removeTrack(this.cameraTrack);
        this.cameraTrack.stop();
        this.cameraTrack = null;
      }
      if (!screenSharing) await this.sendVideo(null);
      useCallStore.setState({ videoEnabled: false, localVersion: callState().localVersion + 1 });
    } else {
      try {
        const camera = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        this.cameraTrack = camera.getVideoTracks()[0] ?? null;
        if (!this.cameraTrack) return;
        localStream?.addTrack(this.cameraTrack);
        if (!screenSharing) await this.sendVideo(this.cameraTrack);
        useCallStore.setState({ videoEnabled: true, localVersion: callState().localVersion + 1 });
      } catch (error) {
        this.events.onMediaError?.('video', error);
        return;
      }
    }
    this.announceMedia();
  }

  async toggleScreen(): Promise<void> {
    if (callState().screenSharing) {
      this.stopScreen();
      return;
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      this.screenTrack = display.getVideoTracks()[0] ?? null;
      if (!this.screenTrack) return;
      this.screenTrack.onended = () => this.stopScreen();
      await this.sendVideo(this.screenTrack);
      useCallStore.setState({ screenSharing: true, localVersion: callState().localVersion + 1 });
      this.announceMedia();
    } catch (error) {
      if ((error as DOMException | undefined)?.name !== 'NotAllowedError')
        this.events.onMediaError?.('screen', error);
    }
  }

  private stopScreen(): void {
    this.screenTrack?.stop();
    this.screenTrack = null;
    void this.sendVideo(callState().videoEnabled ? this.cameraTrack : null);
    useCallStore.setState({ screenSharing: false, localVersion: callState().localVersion + 1 });
    this.announceMedia();
  }

  /** The track shown in this tab's own tile (screen while sharing). */
  get previewTrack(): MediaStreamTrack | null {
    return this.screenTrack ?? this.cameraTrack;
  }

  private async sendVideo(track: MediaStreamTrack | null): Promise<void> {
    await Promise.all(
      [...this.peers.values()].map(async ({ pc }) => {
        const sender = pc.getTransceivers().find((t) => t.receiver.track.kind === 'video')?.sender;
        await sender?.replaceTrack(track).catch(() => undefined);
      }),
    );
  }

  private announceMedia(): void {
    const { call, audioEnabled, videoEnabled, screenSharing } = callState();
    if (call && this.connection?.state === HubConnectionState.Connected)
      this.connection
        .invoke('UpdateMedia', call.id, audioEnabled, videoEnabled, screenSharing)
        .catch(() => undefined);
  }

  // ---- WebRTC ----------------------------------------------------------------------------------------------------

  private peer(remote: string): PeerEntry {
    const existing = this.peers.get(remote);
    if (existing) return existing;
    const pc = new RTCPeerConnection({
      iceServers: this.iceConfig.iceServers,
      iceTransportPolicy: this.iceConfig.iceTransportPolicy,
    });
    const entry: PeerEntry = { pc, stream: new MediaStream(), pending: [] };
    this.peers.set(remote, entry);
    pc.onicecandidate = (event) => {
      if (event.candidate) void this.send(remote, { type: 'ice', candidate: event.candidate.toJSON() });
    };
    pc.ontrack = (event) => {
      entry.stream
        .getTracks()
        .filter((t) => t.kind === event.track.kind)
        .forEach((t) => entry.stream.removeTrack(t));
      entry.stream.addTrack(event.track);
      this.patchPeer(remote, (p) => ({ ...p, stream: entry.stream, streamVersion: p.streamVersion + 1 }));
    };
    pc.onconnectionstatechange = () => this.patchPeer(remote, (p) => ({ ...p, state: pc.connectionState }));
    return entry;
  }

  /** Attaches this tab's tracks to the connection's audio and video transceivers. */
  private async attachLocal(pc: RTCPeerConnection, create: boolean): Promise<void> {
    const local = callState().localStream;
    const audio = local?.getAudioTracks()[0] ?? null;
    const video = this.previewTrack;
    if (create) {
      pc.addTransceiver(audio ?? 'audio', { direction: 'sendrecv', streams: local ? [local] : [] });
      pc.addTransceiver(video ?? 'video', { direction: 'sendrecv', streams: local ? [local] : [] });
      return;
    }
    for (const transceiver of pc.getTransceivers()) {
      const kind = transceiver.receiver.track.kind;
      transceiver.direction = 'sendrecv';
      await transceiver.sender.replaceTrack(kind === 'audio' ? audio : video);
      if (local) transceiver.sender.setStreams(local);
    }
  }

  private async offer(remote: string): Promise<void> {
    try {
      this.iceConfig = await this.ice.current();
      const { pc } = this.peer(remote);
      await this.attachLocal(pc, true);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await this.send(remote, { type: 'offer', sdp: pc.localDescription?.sdp ?? offer.sdp ?? '' });
    } catch (error) {
      console.warn('[calls] offer failed', error);
    }
  }

  private async onSignal(message: SignalMessage): Promise<void> {
    const call = callState().call;
    if (message.callId !== call?.id) return;
    const remote = message.fromConnectionId;
    if (!callState().peers[remote]) {
      // The offer overtook the "participant joined" notice.
      const known = call.participants.find((p) => p.connectionId === remote);
      useCallStore.setState((s) => ({
        peers: {
          ...s.peers,
          [remote]: known
            ? toPeer(known)
            : toPeer({
                connectionId: remote,
                user: { id: message.fromUserId, fullName: '', avatarUrl: null, position: null },
                joinedAt: new Date().toISOString(),
                audio: true,
                video: false,
                screen: false,
              }),
        },
      }));
    }
    if (!this.peers.has(remote)) this.iceConfig = await this.ice.current();
    const entry = this.peer(remote);
    const { pc } = entry;
    try {
      if (message.data.type === 'offer') {
        await pc.setRemoteDescription({ type: 'offer', sdp: message.data.sdp });
        await this.attachLocal(pc, false);
        await this.flush(entry);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await this.send(remote, { type: 'answer', sdp: pc.localDescription?.sdp ?? answer.sdp ?? '' });
      } else if (message.data.type === 'answer') {
        await pc.setRemoteDescription({ type: 'answer', sdp: message.data.sdp });
        await this.flush(entry);
      } else if (pc.remoteDescription) {
        await pc.addIceCandidate(message.data.candidate);
      } else {
        entry.pending.push(message.data.candidate);
      }
    } catch (error) {
      console.warn('[calls] signal failed', error);
    }
  }

  private async flush(entry: PeerEntry): Promise<void> {
    const pending = entry.pending.splice(0);
    for (const candidate of pending) await entry.pc.addIceCandidate(candidate).catch(() => undefined);
  }

  private async send(remote: string, data: SignalData): Promise<void> {
    const call = callState().call;
    if (!call || !this.connection) return;
    await this.connection.invoke('Signal', call.id, remote, data).catch(() => undefined);
  }

  // ---- hub events --------------------------------------------------------------------------------------------------

  private onParticipantJoined({
    callId,
    participant,
  }: {
    callId: string;
    participant: CallParticipant;
  }): void {
    const { call, peers } = callState();
    if (call?.id !== callId) return;
    // A rejoin from a new connection replaces the old one.
    const stale = Object.values(peers).filter(
      (p) => p.user.id === participant.user.id && p.connectionId !== participant.connectionId,
    );
    for (const peer of stale) this.closePeer(peer.connectionId);
    useCallStore.setState((s) => {
      const existing = s.peers[participant.connectionId];
      return {
        peers: {
          ...s.peers,
          // A peer created by an offer that overtook this notice only knew the user id: take name and media state now.
          [participant.connectionId]: !existing
            ? toPeer(participant)
            : existing.user.fullName
              ? existing
              : {
                  ...existing,
                  user: participant.user,
                  audio: participant.audio,
                  video: participant.video,
                  screen: participant.screen,
                },
        },
        call: s.call && !s.call.connectedAt ? { ...s.call, connectedAt: new Date().toISOString() } : s.call,
      };
    });
  }

  private onParticipantLeft({
    callId,
    connectionId,
  }: {
    callId: string;
    userId: string;
    connectionId: string;
  }): void {
    if (callState().call?.id !== callId) return;
    this.closePeer(connectionId);
  }

  private onMediaChanged(payload: {
    callId: string;
    userId: string;
    audio: boolean;
    video: boolean;
    screen: boolean;
  }): void {
    if (callState().call?.id !== payload.callId) return;
    useCallStore.setState((s) => ({
      peers: Object.fromEntries(
        Object.entries(s.peers).map(([id, p]) =>
          p.user.id === payload.userId
            ? [id, { ...p, audio: payload.audio, video: payload.video, screen: payload.screen }]
            : [id, p],
        ),
      ),
    }));
  }

  /** The server's view of the call changed (participants, connected time). */
  syncCall(call: Call): void {
    const current = callState().call;
    if (current?.id !== call.id) return;
    useCallStore.setState({ call: { ...call } });
  }

  private closePeer(connectionId: string): void {
    this.peers.get(connectionId)?.pc.close();
    this.peers.delete(connectionId);
    useCallStore.setState((s) => {
      const { [connectionId]: _removed, ...rest } = s.peers;
      return { peers: rest };
    });
  }

  private patchPeer(connectionId: string, update: (peer: RemotePeer) => RemotePeer): void {
    useCallStore.setState((s) => {
      const peer = s.peers[connectionId];
      return peer ? { peers: { ...s.peers, [connectionId]: update(peer) } } : s;
    });
  }
}

function toPeer(participant: CallParticipant): RemotePeer {
  return {
    connectionId: participant.connectionId,
    user: participant.user,
    audio: participant.audio,
    video: participant.video,
    screen: participant.screen,
    stream: null,
    streamVersion: 0,
    state: 'waiting',
  };
}

/** The one call manager of this tab. */
export const callManager = new CallManager();
